import { DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    LsqApiServiceDataSource,
    LsqApiServiceListSource,
    LsqDataSource,
    Pagination,
    SortDirectionFinancing
} from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    FastTrackRouteQueryParams,
    FeatureFlags
} from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    ACCOUNT_SUPPLIER_DETAILS,
    atlas,
    BUYER_ACCOUNT_INVOICE_DETAIL,
    BUYER_SUPPLIER_DETAILS,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_PARENT_GROUPED_INVOICE_DETAIL,
    SUPPLY_CHAIN_DIRECT_BUYER_INVOICES,
    SUPPLY_CHAIN_DIRECT_BUYER_PARENT_GROUPED_INVOICE_DETAIL
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    InvoiceSummary,
    ListGroupedInvoiceSummaryForBuyerAccountRequest,
    ListGroupedInvoiceSummaryForBuyerRequest,
    RevokeInvoiceApprovalRequest,
    RevokeInvoiceApprovalService
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import {
    ListAllSupplierNamesAndIdsRequest,
    ListAllSupplierNamesAndIdsService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/invoice-list/list-all-supplier-names-and-ids';
import {
    RevokeChildInvoiceRequest,
    RevokeChildInvoiceService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/revoke-child-invoice/revoke-child-invoice';
import { DashboardClaim, Guid } from '@lsq-frontend/remote-services/enterprise/common';
import {
    PutPayableStatusService,
    PayableStatus,
    PutApprovalStatusService,
    PutCreditMemosStatusService
} from '@lsq-frontend/remote-services/inv-restapi';
import {
    BuyerInvoiceStatus2,
    GetInvoiceSummaryRequest,
    GetInvoiceSummaryService,
    InvoiceSummary2,
    InvoiceSummaryDto,
    ListGroupedInvoiceSummaryForBuyerAccountRequest2,
    ListGroupedInvoiceSummaryForBuyerAccountService,
    ListGroupedInvoiceSummaryForBuyerRequest2,
    ListGroupedInvoiceSummaryForBuyerService,
    ListInvoiceSummaryForBuyerAccountRequest2,
    ListInvoiceSummaryForBuyerAccountService,
    ListInvoiceSummaryForBuyerRequest2,
    ListInvoiceSummaryForBuyerService
} from '@lsq-frontend/remote-services/financing-graphql';
import {
    Color,
    DateFormat,
    forLifeOf,
    getDynamicRouteLink,
    getDynamicRouterLink,
    getRouterLinkById,
    ignoreNullish,
    Interaction,
    ISelectOption,
    LifecycleAware,
    Modal,
    StatusColor,
    Table,
    TableAppearance,
    TableHelper,
    TabsConfig
} from '@lsq-frontend/common-ui';
import {
    ClaimService,
    FeatureFlagService,
    getRouterLinkUrlById,
    Icons,
    LsqDatePipe,
    ResponsiveService,
    WindowReferenceService
} from '@lsq-frontend/common-ui';
import { LsqMultiSelectComponent } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { isEqual } from 'lodash';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
    debounceTime,
    delay,
    distinctUntilChanged,
    filter,
    first,
    map,
    mapTo,
    pluck,
    skip,
    startWith,
    switchMap,
    withLatestFrom
} from 'rxjs/operators';
import { TAKE_ALL } from '../../../constants';
import { InvoiceFundingStatusDisplay } from '../../supply-chain-direct.models';
import { SupplyChainDirectAccountService } from '../supply-chain-direct-account.service';
import { SupplyChainDirectBuyerAccountSharedService } from '../supply-chain-direct-buyer-account-shared.service';
import { InvoicesFilterType, LoadableInvoiceSummary } from './invoices.helpers';
import { RevokeInvoiceModalComponent } from './revoke-invoice-modal/revoke-invoice-modal.component';
import {
    GetBuyerAccountInvoiceExportService,
    GetBuyerInvoiceExportService
} from '@lsq-frontend/remote-services/report-center';
@Component({
    selector: 'supply-chain-direct-account-invoices',
    templateUrl: './invoices.component.html',
    styleUrls: ['./invoices.component.scss'],
    encapsulation: ViewEncapsulation.None
})
@LifecycleAware()
export class AccountInvoicesComponent implements OnInit, OnDestroy {
    @ViewChild(LsqMultiSelectComponent)
    public sellerKeysMultiSelectComponent!: LsqMultiSelectComponent;

    public _invoiceSummaryRawData$ = this._getInvoiceSummaryService.dataStream.pipe(
        pluck('invoiceSummary'),
        ignoreNullish()
    );

    public initialized = false;
    public ineligibleStatus = 'INELIGIBLE';

    public readonly invoiceNumberTruncateAfter$ = this.windowReferenceService.resizeStream.pipe(
        map((dimensions) => {
            if (dimensions.width > 1525) {
                return 15;
            }

            if (dimensions.width > 1375) {
                return 12;
            }

            if (dimensions.width > 990) {
                return 8;
            }
            return 10;
        })
    );
    public readonly invoiceSummaryDataSource = new LsqDataSource<InvoiceSummaryDto>({
        data$: this._invoiceSummaryRawData$.pipe(
            map((invoiceSummary) => {
                if (invoiceSummary === undefined) {
                    return {
                        totalEarlyPaid: {
                            cents: 0,
                            currencyCode: 'USD'
                        },
                        earlyPaidInvoiceCount: 0,
                        totalEligible: {
                            cents: 0,
                            currencyCode: 'USD'
                        },
                        eligibleInvoiceCount: 0,
                        totalIneligible: {
                            cents: 0,
                            currencyCode: 'USD'
                        },
                        ineligibleInvoiceCount: 0,
                        totalOpen: {
                            cents: 0,
                            currencyCode: 'USD'
                        },
                        totalOpenInvoiceCount: 0,
                        totalRevoked: {
                            cents: 0,
                            currencyCode: 'USD'
                        }
                    };
                }

                return invoiceSummary;
            })
        ),
        loading$: this._invoiceSummaryRawData$.pipe(mapTo(false), startWith(true))
    });
    public dataPoints$ = this.invoiceSummaryDataSource.data$.pipe(
        forLifeOf(this),
        ignoreNullish(),
        withLatestFrom(this._supplyChainAccountSharedService.isPayLaterEnabled$),
        map(([invoiceSummary, payLaterEnabled]) => {
            const dataPoints = [
                {
                    label: this._i18n('Open Eligible'),
                    data: this._decimalPipe.transform(invoiceSummary?.eligibleInvoiceCount) ?? '0'
                },
                {
                    label: this._i18n('Open Ineligible'),
                    data: this._decimalPipe.transform(invoiceSummary?.ineligibleInvoiceCount) ?? '0'
                },
                {
                    label: this._i18n('Open Early Paid'),
                    data: this._decimalPipe.transform(invoiceSummary?.earlyPaidInvoiceCount) ?? '0'
                }
            ];

            if (payLaterEnabled) {
                dataPoints.push({
                    label: this._i18n('Open FlexTerm'),
                    data: this._decimalPipe.transform(invoiceSummary?.payLaterInvoiceCount) ?? '0'
                });
            }

            dataPoints.push({
                label: this._i18n('Total Open Invoices'),
                data: this._decimalPipe.transform(invoiceSummary?.totalOpenInvoiceCount) ?? '0'
            });

            return dataPoints;
        })
    );

    public listInvoicesDataSource: LsqApiServiceListSource<any, any, InvoiceSummary2>;
    public readonly listSupplierNamesDataSource = new LsqApiServiceDataSource(
        this._listAllSupplierNamesAndIdsService,
        new ListAllSupplierNamesAndIdsRequest({
            buyerKeysFilter: []
        })
    );
    public readonly supplierFilterOptions$ = this.listSupplierNamesDataSource.data$.pipe(
        map((response) =>
            response.supplierNames!.map((sellerNameAndId) => ({
                label: sellerNameAndId.name!,
                value: sellerNameAndId.sellerKey!
            }))
        )
    );
    public invoiceGroupTogglesAreDisabled = false;
    public readonly tableAppearance = TableAppearance;
    public readonly color = Color;
    public readonly icons = Icons;
    public readonly featureFlags = FeatureFlags;
    public reportName = `Invoices_${this._lsqDatePipe
        .transform(new Date(), 'shortDate')
        .replace(/\//g, '-')}`;
    public readonly lsqShortDateFormat = DateFormat.LsqShort;
    public readonly debounceTime = Interaction.SearchDebounceTimeMs;
    public readonly processingColor = Color.Yellow;
    public readonly processingInvoiceStatus = this._i18n({
        value: 'Processing'
    });
    public readonly statusColor = StatusColor as unknown as Record<string, StatusColor>;
    public readonly creditMemoStatus = 'CREDIT_MEMO';
    public readonly buyerInvoiceStatus = BuyerInvoiceStatus2;
    public readonly allInvoicesI18n = this._i18n({
        value: 'All Invoices',
        meaning: `Filter option to view all invoices from suppliers`,
        description: 'Page title'
    });
    public readonly yourInvoicesI18n = this._i18n({
        value: 'Your Invoices',
        meaning: `Your (the buyer's) invoices from suppliers`,
        description: 'Page title'
    });
    public readonly businessUnitNameI18n = this._i18n({
        value: 'Business Unit',
        meaning: `Business unit's name`,
        description: 'Table column header'
    });
    public readonly supplierNameI18n = this._i18n({
        value: 'Supplier',
        meaning: `Seller's name`,
        description: 'Table column header'
    });
    public readonly invoiceNumberI18n = this._i18n({
        value: 'Invoice Number',
        meaning: `Invoice number`,
        description: 'Table column header'
    });
    public readonly invoiceDateI18n = this._i18n({
        value: 'Invoice Date',
        meaning: `Invoice date`,
        description: 'Table column header'
    });
    public readonly earlyPaidDateI18n = this._i18n({
        value: 'Early Paid Date',
        meaning: `Date the invoice was paid to the supplier by LSQ`,
        description: 'Table column header'
    });
    public readonly dueDateI18n = this._i18n({
        value: 'Due Date',
        meaning: `Date the invoice is due to be paid by the buyer`,
        description: 'Table column header'
    });
    public readonly buyerDiscountPercentageI18n = this._i18n({
        value: 'Fee Share',
        meaning: `The percent the buyer pays of the supplier fees`,
        description: 'Table column header'
    });
    public readonly buyerDiscountAmountI18n = this._i18n({
        value: 'Buyer Fee',
        meaning: `The amount the buyer has paid for the supplier fees`,
        description: 'Table column header'
    });
    public readonly invoiceAmountI18n = this._i18n({
        value: 'Invoice Amount',
        meaning: `The money value of the invoice`,
        description: 'Table column header'
    });
    public readonly invoiceStatusI18n = this._i18n({
        value: 'Status',
        meaning: `The eligibility status of the invoice`,
        description: 'Table column header'
    });
    public readonly invoiceFeesI18n = this._i18n({
        value: 'Fees Due',
        meaning: `Any additional charges accrued by the buyer on the invoice, including late fees`,
        description: 'Table column header'
    });
    public readonly payLaterDueDateI18n = this._i18n({
        value: 'New Due Date',
        meaning: `The extended due date for a "flex term" invoice`,
        description: 'Table column header'
    });
    public readonly feeShareToolTipTextI18n = this._i18n({
        value: "The displayed fee rates are what you're paying towards your suppliers early payment fees."
    });

    public readonly revokeHeaderI18n = this._i18n({ value: 'Revoke' });
    public readonly revokeModalTitleI18n = this._i18n({ value: 'Revoke Invoice' });
    public readonly revokeGroupModalTitleI18n = this._i18n({ value: 'Revoke Invoices' });
    public invoiceStatusFilterOptions: ISelectOption[] = [
        {
            label: this._i18n('Eligible'),
            selectedLabel: this._i18n('Eligible'),
            value: InvoicesFilterType.OnlyAvailable
        },
        {
            label: this._i18n('Early Paid'),
            selectedLabel: this._i18n('Early Paid'),
            value: InvoicesFilterType.OnlyPaid
        },
        {
            label: this._i18n('FlexTerm'),
            selectedLabel: this._i18n('FlexTerm'),
            value: InvoicesFilterType.OnlyPayLater
        },
        {
            label: this._i18n('Pending'),
            selectedLabel: this._i18n('Pending'),
            value: InvoicesFilterType.OnlyPending
        },
        {
            label: this._i18n('Closed'),
            selectedLabel: this._i18n('Closed'),
            value: InvoicesFilterType.OnlyClosed
        },
        {
            label: this._i18n('Ineligible'),
            selectedLabel: this._i18n('Ineligible'),
            value: InvoicesFilterType.OnlyExpired
        },
        {
            label: this._i18n('Revoked'),
            selectedLabel: this._i18n('Revoked'),
            value: InvoicesFilterType.OnlyRevoked
        }
    ];
    public readonly getRowClassNameBound = this._getRowClassName.bind(this);
    public readonly revokeButtonIsHiddenBound = this._revokeButtonIsHidden.bind(this);
    public readonly revokeButtonIsDisabledBound = this._revokeButtonIsDisabled.bind(this);
    public readonly openInvoiceDetailsBound = this._openInvoiceDetail.bind(this);
    public readonly isInvoiceClickableBound = this.isInvoiceClickable.bind(this);
    public readonly goSupplierDetailsBound = this.goSupplierDetails.bind(this);

    public isFinancingAccountPortalUser = false;

    public readonly invoicesFiltersFormGroup = new FormGroup({
        invoicesFilter: new FormControl(InvoicesFilterType.ShowAll),
        invoiceNumber: new FormControl(),
        sellerKeys: new FormControl([]),
        buyerKeys: new FormControl([])
    });
    public readonly expandedGroupInvoiceKeys = new Set<string>();
    public readonly nullValue = Table.NullAmount;
    public readonly allSuppliersI18n = this._i18n('All Suppliers');
    public readonly selectSupplierI18n = this._i18n('Select Supplier');
    public readonly allStatusesI18n = this._i18n('All Invoices');
    public readonly selectStatusI18n = this._i18n('Select Status');
    public readonly tabsConfig$: Observable<TabsConfig[]> = this._claimService
        .isOn(DashboardClaim.FinancingAccountPortal)
        .pipe(
            map((isAccountPortalUser) => [
                {
                    label: this._i18n('Invoices'),
                    routerLink: getDynamicRouteLink(
                        getRouterLinkUrlById(
                            atlas,
                            isAccountPortalUser
                                ? SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES
                                : SUPPLY_CHAIN_DIRECT_BUYER_INVOICES
                        ),
                        {}
                    ),
                    queryParams: {
                        tab: FastTrackRouteQueryParams.InvoicesTab
                    }
                },
                {
                    label: this._i18n('Pending Credit Memos'),
                    routerLink: getDynamicRouteLink(
                        getRouterLinkUrlById(
                            atlas,
                            isAccountPortalUser
                                ? SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES
                                : SUPPLY_CHAIN_DIRECT_BUYER_INVOICES
                        ),
                        {}
                    ),
                    queryParams: {
                        tab: FastTrackRouteQueryParams.PendingCreditMemosTab
                    }
                }
            ])
        );
    public activeTabLabel$ = this._activatedRoute.queryParams.pipe(
        map((queryParams) => queryParams.tab)
    );
    public routeQueryParams = FastTrackRouteQueryParams;
    public onlyOpenAndRevokedCreditMemos: boolean;

    constructor(
        public invoiceFundingStatusDisplay: InvoiceFundingStatusDisplay,
        public responsiveService: ResponsiveService,
        public getBuyerAccountInvoiceExportService: GetBuyerAccountInvoiceExportService,
        public getBuyerInvoiceSummaryExportService: GetBuyerInvoiceExportService,
        private _claimService: ClaimService,
        private _listInvoiceSummaryForBuyerAccountService: ListInvoiceSummaryForBuyerAccountService,
        private _listGroupedInvoiceSummaryForBuyerAccountService: ListGroupedInvoiceSummaryForBuyerAccountService,
        private _listInvoiceSummaryForBuyerService: ListInvoiceSummaryForBuyerService,
        private _listGroupedInvoiceSummaryForBuyerService: ListGroupedInvoiceSummaryForBuyerService,
        private _i18n: I18n,
        private _lsqDatePipe: LsqDatePipe,
        private _modalService: ModalService,
        private _revokeInvoiceApprovalService: RevokeInvoiceApprovalService,
        private _revokeChildInvoiceService: RevokeChildInvoiceService,
        private _putPayableStatusService: PutPayableStatusService,
        private _putApprovalStatusService: PutApprovalStatusService,
        private _putCreditMemosStatusService: PutCreditMemosStatusService,
        private _userService: UserService,
        private _listAllSupplierNamesAndIdsService: ListAllSupplierNamesAndIdsService,
        private _supplyChainDirectAccountService: SupplyChainDirectAccountService,
        private _supplyChainAccountSharedService: SupplyChainDirectBuyerAccountSharedService,
        private _getInvoiceSummaryService: GetInvoiceSummaryService,
        private _decimalPipe: DecimalPipe,
        private _activatedRoute: ActivatedRoute,
        private _featureFlagService: FeatureFlagService,
        private _router: Router,
        public buyerAccountSharedService: SupplyChainDirectBuyerAccountSharedService,
        public windowReferenceService: WindowReferenceService
    ) {
        this._activatedRoute.queryParams.subscribe((queryParams) => {
            // Set the default tab's query param by default if none selected.
            if (
                this._featureFlagService.isOnSync(FeatureFlags.PendingCreditMemos) &&
                !queryParams.tab
            ) {
                this._router.navigate([], {
                    relativeTo: this._activatedRoute,
                    queryParams: { tab: FastTrackRouteQueryParams.InvoicesTab },
                    replaceUrl: true
                });
            }
        });
    }

    public ngOnInit(): void {
        // Activate the stream.
        this.supplierFilterOptions$.pipe(forLifeOf(this)).subscribe();

        const defaultPagination = {
            skip: 0,
            take: 25,
            sortColumn: 'invoiceDate',
            sortDirection: SortDirectionFinancing.DESCENDING,
            secondarySortColumn: 'sellerName',
            secondarySortDirection: SortDirectionFinancing.ASCENDING
        };
        const queryParamMap = this._activatedRoute.snapshot.queryParamMap;

        const pagination = TableHelper.getPaginationByQueryParams(
            this._activatedRoute.snapshot.queryParamMap
        );

        const invoicesFilter = InvoicesFilterType[
            queryParamMap.get(FastTrackRouteQueryParams.InvoiceTypeFilter) as unknown as number
        ] as keyof typeof InvoicesFilterType;
        const invoiceNumber = queryParamMap.get(FastTrackRouteQueryParams.SearchText) ?? undefined;
        const sellerKeys = queryParamMap.getAll(FastTrackRouteQueryParams.SupplierKeysFilter) ?? [];
        const buyerKeys = queryParamMap.getAll(FastTrackRouteQueryParams.BuyerKeysFilter) ?? [];

        if (invoicesFilter || invoiceNumber || sellerKeys || buyerKeys) {
            this.invoicesFiltersFormGroup.patchValue(
                {
                    invoicesFilter: InvoicesFilterType[invoicesFilter],
                    invoiceNumber: invoiceNumber,
                    sellerKeys: sellerKeys,
                    buyerKeys: buyerKeys
                },
                {
                    onlySelf: true,
                    emitEvent: false
                }
            );
        }

        this.isFinancingAccountPortalUser = this._claimService.isOnSync(
            DashboardClaim.FinancingAccountPortal
        );
        if (this.isFinancingAccountPortalUser) {
            this.listInvoicesDataSource = new LsqApiServiceListSource(
                this._listInvoiceSummaryForBuyerAccountService,
                new ListInvoiceSummaryForBuyerAccountRequest2({
                    refAccountKey: this._getAlternateCompanyKey(),
                    invoiceNumberFilter: invoiceNumber,
                    includePendingPayables: true,
                    pagination: {
                        ...pagination,
                        ...defaultPagination
                    } as Pagination,
                    sellerKeys: sellerKeys,
                    buyerKeys: buyerKeys,
                    buyerInvoiceStatus: this._getBuyerInvoiceStatusFromFilter(
                        invoicesFilter ? [InvoicesFilterType[invoicesFilter]] : undefined
                    )
                })
            );
        } else {
            this.listInvoicesDataSource = new LsqApiServiceListSource(
                this._listInvoiceSummaryForBuyerService,
                new ListInvoiceSummaryForBuyerRequest2({
                    refBuyerKeys: [this._getAlternateCompanyKey()],
                    sellerKeys: sellerKeys,
                    invoiceNumberFilter: invoiceNumber,
                    includePendingPayables: true,
                    pagination: {
                        ...pagination,
                        ...defaultPagination
                    } as Pagination,
                    buyerInvoiceStatus: this._getBuyerInvoiceStatusFromFilter(
                        invoicesFilter ? [InvoicesFilterType[invoicesFilter]] : undefined
                    )
                })
            );
        }
        if (this._featureFlagService.isOnSync(FeatureFlags.BuyerInvoicesKPIs)) {
            this._getInvoiceSummaryService.execute(
                new GetInvoiceSummaryRequest({
                    buyerKeys:
                        this._supplyChainDirectAccountService.buyerKeysFormGroup.get('buyerKeys')
                            ?.value ?? []
                })
            );
            this.invoiceSummaryDataSource.connect();
        }

        // Any time we ask for new invoices, collapse all open groups.
        this.listInvoicesDataSource.loading$
            ?.pipe(
                forLifeOf(this),
                filter((loading) => loading === true),
                switchMap(() => this._collapseAll())
            )
            .subscribe();

        // Dispatch a new request for invoices whenever the `invoicesFilter` changes, and
        // reset the page to 1.
        this.invoicesFiltersFormGroup.controls.invoicesFilter.valueChanges
            .pipe(
                forLifeOf(this),
                debounceTime(this.debounceTime),
                filter(
                    (newFilter) =>
                        !this.listInvoicesDataSource.request.buyerInvoiceStatus?.includes(newFilter)
                )
            )
            .subscribe((invoicesFilter) => {
                this.listInvoicesDataSource.dispatchRequest({
                    ...this.listInvoicesDataSource.request,
                    buyerInvoiceStatus: this._getBuyerInvoiceStatusFromFilter(invoicesFilter),
                    pagination: {
                        ...this.listInvoicesDataSource.request.pagination,
                        skip: 0
                    }
                });
            });

        // Dispatch a new request for invoices whenever the `sellerKeys` filter changes, and
        // reset the page to 1.
        this.invoicesFiltersFormGroup.controls.sellerKeys.valueChanges
            .pipe(
                forLifeOf(this),
                debounceTime(this.debounceTime),
                filter(
                    (sellerKeys) =>
                        !isEqual(sellerKeys, this.listInvoicesDataSource.request.sellerKeys)
                )
            )
            .subscribe((sellerKeys) => {
                this.listInvoicesDataSource.dispatchRequest({
                    ...this.listInvoicesDataSource.request,
                    sellerKeys: sellerKeys ?? [],
                    pagination: {
                        ...this.listInvoicesDataSource.request.pagination,
                        skip: 0
                    }
                });
            });

        // Dispatch new requests whenever the `buyerKeys` filter changes, and
        // reset the page to 1. First, synchronize the SupplyChainDirectAccountComponent's
        // `buyerKeysFormGroup` with our `invoicesFiltersFormGroup`.
        this._supplyChainDirectAccountService.buyerKeysFormGroup
            .get('buyerKeys')
            ?.valueChanges.pipe(forLifeOf(this))
            .subscribe((buyerKeys) => {
                this.invoicesFiltersFormGroup.get('buyerKeys')?.setValue(buyerKeys);
            });
        this.invoicesFiltersFormGroup.controls.buyerKeys.valueChanges
            .pipe(
                forLifeOf(this),
                debounceTime(this.debounceTime),
                filter(
                    (buyerKeys) =>
                        !isEqual(buyerKeys, this.listInvoicesDataSource.request.buyerKeys)
                )
            )
            .subscribe((buyerKeys) => {
                this._getInvoiceSummaryService.execute(new GetInvoiceSummaryRequest({ buyerKeys }));
                this.listInvoicesDataSource.dispatchRequest({
                    ...this.listInvoicesDataSource.request,
                    buyerKeys: buyerKeys ?? [],
                    pagination: {
                        ...this.listInvoicesDataSource.request.pagination,
                        skip: 0
                    }
                });
                this.listSupplierNamesDataSource.dispatchRequest({
                    ...this.listSupplierNamesDataSource.request,
                    buyerKeysFilter: buyerKeys ?? []
                });
            });

        // Dispatch a new request for invoices whenever the `invoiceNumber` search changes,
        // and expand any grouped invoices that match the search.
        this.invoicesFiltersFormGroup.controls.invoiceNumber.valueChanges
            .pipe(
                forLifeOf(this),
                distinctUntilChanged(),
                debounceTime(this.debounceTime),
                switchMap((invoiceNumberFilter) => {
                    this.listInvoicesDataSource.dispatchRequest({
                        invoiceNumberFilter: invoiceNumberFilter
                    });
                    // `data$` replays its last value, so we need to skip that and wait for the new
                    // data to be emitted. Using `data$` and not `rows$` here because `rows$` emits
                    // a new value whenever a group is expanded, and we want to ignore that
                    // behavior in this logic.
                    return this.listInvoicesDataSource.data$.pipe(skip(1), first());
                }),
                // Only keep going if there are grouped invoices that match the search.
                filter(({ rows }: { rows: InvoiceSummary[] }) =>
                    rows.some((invoiceRow) => !!invoiceRow.groupedInvoiceNumberMatched)
                ),
                // Wait a tick for the table to fully render, since we're about to try to expand
                // grouped invoice rows.
                delay(0),
                // If there are matching grouped invoices, expand their groups.
                switchMap(async ({ rows }) => {
                    const filteredRows = rows.filter(
                        (invoiceRow) => invoiceRow.groupedInvoiceNumberMatched
                    );
                    for (const row of filteredRows) {
                        await this._expandGroup(row.refInvoiceKey);
                    }
                })
            )
            .subscribe();

        combineLatest([
            this.buyerAccountSharedService.canRevokeInvoices$,
            this.buyerAccountSharedService.isBuyerPaidEarlyPaymentFeePercentageShown$
        ])
            .pipe(forLifeOf(this))
            .subscribe(async ([]) => {
                this.initialized = true;
            });
        this.listInvoicesDataSource.data$.subscribe((data) => {
            this.setFilterQueryParams();
            this.checkOpenAndRevokedCreditMemos(data.rows);
        });

        if (!this._featureFlagService.isOnSync(FeatureFlags.PayLater)) {
            const indexOfPayLater = this.invoiceStatusFilterOptions.findIndex(
                ({ value }) => value === InvoicesFilterType.OnlyPayLater
            );
            this.invoiceStatusFilterOptions.splice(indexOfPayLater, 1);
        }
    }

    public ngOnDestroy(): void {}

    public async handleRevokeClick(row: InvoiceSummary2): Promise<void> {
        const isSubmittingSubject = new BehaviorSubject<boolean>(false);
        this._modalService.showModal(
            new Modal({
                id: 'revoke-invoice',
                title: row.isGroup ? this.revokeGroupModalTitleI18n : this.revokeModalTitleI18n,
                component: RevokeInvoiceModalComponent,
                width: '60rem',
                showCloseButton: true,
                data: {
                    buyerInvoice: row,
                    childInvoices: row.isGroup ? await this.getGroupInvoices(row) : []
                },
                isSubmitting$: isSubmittingSubject.asObservable(),
                onOkClick: async (): Promise<void> => {
                    isSubmittingSubject.next(true);
                    const isPayLater = this._featureFlagService.isOnSync(FeatureFlags.PayLater);
                    if (row.refGroupedInvoiceKey) {
                        if (isPayLater) {
                            if (row.invoiceAmount.cents < 0) {
                                //Is credit memo
                                await this._putCreditMemosStatusService.execute(
                                    row.refGroupedInvoiceKey,
                                    PayableStatus.Rejected
                                );
                            } else {
                                //is approval
                                await this._putApprovalStatusService.execute(
                                    row.refGroupedInvoiceKey,
                                    PayableStatus.Rejected
                                );
                            }
                        } else {
                            await this._revokeChildInvoiceService.executeOnce(
                                new RevokeChildInvoiceRequest({
                                    childInvoiceKey: row.refGroupedInvoiceKey
                                })
                            );
                        }
                    } else {
                        if (isPayLater) {
                            await this._putPayableStatusService.execute(
                                row.refInvoiceKey,
                                PayableStatus.Rejected
                            );
                        } else {
                            await this._revokeInvoiceApprovalService.executeOnce(
                                new RevokeInvoiceApprovalRequest({
                                    accountKey: this._getAlternateCompanyKey(),
                                    invoiceKeys: [row.refInvoiceKey]
                                })
                            );
                        }
                    }
                    isSubmittingSubject.next(false);

                    this._modalService.closeModal();
                    this.listInvoicesDataSource.dispatchRequest();
                }
            })
        );
    }

    private async getGroupInvoices(row: InvoiceSummary2): Promise<InvoiceSummary2[]> {
        const listGroupedBuyerInvoiceResponse = this.isFinancingAccountPortalUser
            ? await this._listGroupedInvoiceSummaryForBuyerAccountService.executeOnce(
                  new ListGroupedInvoiceSummaryForBuyerAccountRequest2({
                      accountKey: this._getAlternateCompanyKey(),
                      refInvoiceKey: row.refInvoiceKey,
                      canRevoke: this._featureFlagService.isOnSync(
                          this.featureFlags.RevokeCreditMemo
                      ),
                      pagination: {},
                      includePendingPayables: true
                  })
              )
            : await this._listGroupedInvoiceSummaryForBuyerService.executeOnce(
                  new ListGroupedInvoiceSummaryForBuyerRequest2({
                      buyerKey: this._getAlternateCompanyKey(),
                      refInvoiceKey: row.refInvoiceKey,
                      canRevoke: this._featureFlagService.isOnSync(
                          this.featureFlags.RevokeCreditMemo
                      ),
                      pagination: {},
                      includePendingPayables: true
                  })
              );

        return listGroupedBuyerInvoiceResponse.rows.filter((row) => row.canBeRevoked);
    }

    public async handleInvoiceGroupToggleClick(row: InvoiceSummary): Promise<void> {
        if (this.invoiceGroupTogglesAreDisabled) {
            return;
        }
        this.invoiceGroupTogglesAreDisabled = true;
        if (this.expandedGroupInvoiceKeys.has(row.refInvoiceKey)) {
            await this._collapseGroup(row.refInvoiceKey);
        } else {
            await this._expandGroup(row.refInvoiceKey);
        }
        this.invoiceGroupTogglesAreDisabled = false;
    }

    private async _expandGroup(refInvoiceKey: string): Promise<void> {
        const canRevoke = this._featureFlagService.isOnSync(this.featureFlags.RevokeCreditMemo);
        // Collapse the group if it was somehow already open.
        if (this.expandedGroupInvoiceKeys.has(refInvoiceKey)) {
            await this._collapseGroup(refInvoiceKey);
        }

        // Make sure we're expanding a valid `refInvoiceKey`.
        let invoiceSummaryRows = await this.listInvoicesDataSource.rows$.pipe(first()).toPromise();
        let index = invoiceSummaryRows.findIndex((row) => row.refInvoiceKey === refInvoiceKey);
        if (index < 0) {
            return;
        }

        // Expand the row visually, and add the "loading" row.
        this.expandedGroupInvoiceKeys.add(refInvoiceKey);
        const loadingRow = new LoadableInvoiceSummary({
            isRowLoading: true,
            refInvoiceKey: refInvoiceKey,
            refGroupedInvoiceKey: 'loading' // So we get the grouped invoice styling
        });
        this.listInvoicesDataSource.expandGroup(index, [loadingRow]);

        // Get the grouped invoices.
        const { rows: groupedInvoices } = this.isFinancingAccountPortalUser
            ? await this._listGroupedInvoiceSummaryForBuyerAccountService.executeOnce(
                  new ListGroupedInvoiceSummaryForBuyerAccountRequest2({
                      accountKey: this._getAlternateCompanyKey(),
                      refInvoiceKey: refInvoiceKey,
                      canRevoke,
                      pagination: {
                          skip: 0,
                          take: TAKE_ALL,
                          sortColumn: 'invoiceDate',
                          sortDirection: SortDirectionFinancing.DESCENDING,
                          // TODO(dmayer): Make these fields optional in the proto.
                          secondarySortColumn: '',
                          secondarySortDirection: SortDirectionFinancing.DESCENDING,
                          tertiarySortColumn: '',
                          tertiarySortDirection: SortDirectionFinancing.DESCENDING
                      },
                      includePendingPayables: true
                  })
              )
            : await this._listGroupedInvoiceSummaryForBuyerService.executeOnce(
                  new ListGroupedInvoiceSummaryForBuyerRequest2({
                      buyerKey: this._getAlternateCompanyKey(),
                      refInvoiceKey: refInvoiceKey,
                      canRevoke,
                      pagination: {
                          skip: 0,
                          take: TAKE_ALL,
                          sortColumn: 'invoiceDate',
                          sortDirection: SortDirectionFinancing.DESCENDING,
                          // TODO(dmayer): Make these fields optional in the proto.
                          secondarySortColumn: '',
                          secondarySortDirection: SortDirectionFinancing.DESCENDING,
                          tertiarySortColumn: '',
                          tertiarySortDirection: SortDirectionFinancing.DESCENDING
                      },
                      includePendingPayables: true
                  })
              );

        // Collapse the loading row.
        await this.listInvoicesDataSource.collapseGroup('refInvoiceKey', refInvoiceKey);

        // Expand the group for real.
        invoiceSummaryRows = await this.listInvoicesDataSource.rows$.pipe(first()).toPromise();
        index = invoiceSummaryRows.findIndex((row) => row.refInvoiceKey === refInvoiceKey);
        if (index < 0) {
            return;
        }
        this.listInvoicesDataSource.expandGroup(index, groupedInvoices);
    }

    private async _collapseGroup(refInvoiceKey: string): Promise<void> {
        await this.listInvoicesDataSource.collapseGroup('refInvoiceKey', refInvoiceKey);
        this.expandedGroupInvoiceKeys.delete(refInvoiceKey);
    }

    private async _collapseAll(): Promise<void> {
        for (const invoiceKey of Array.from(this.expandedGroupInvoiceKeys)) {
            await this._collapseGroup(invoiceKey);
        }
    }

    public trackByInvoiceKey(_index: number, { refInvoiceKey }: InvoiceSummary): Guid {
        return refInvoiceKey;
    }

    private _getAlternateCompanyKey(): string {
        return this._userService.getAlternateCompanyKey();
    }

    private _getRowClassName(row: InvoiceSummary): string {
        const classList = [];
        if (row.isGroup) {
            classList.push('is-group');

            if (this.expandedGroupInvoiceKeys.has(row.refInvoiceKey)) {
                classList.push('expanded');
            }
        }
        if (row.refGroupedInvoiceKey) {
            classList.push('is-member-of-group');
        }
        return classList.join(' ');
    }

    private _revokeButtonIsHidden(row: InvoiceSummary): boolean {
        return !(row.canBeRevoked as boolean);
    }

    private _revokeButtonIsDisabled(): boolean {
        return (
            !this._claimService.isOnSync(DashboardClaim.FinancingAccountPortalEditInvoices) &&
            !this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortalEditInvoices)
        );
    }

    private _getBuyerInvoiceStatusFromFilter(
        invoicesFilters: InvoicesFilterType[] = [InvoicesFilterType.ShowAll]
    ): BuyerInvoiceStatus2[] {
        let buyerInvoiceStatuses: BuyerInvoiceStatus2[] = [];
        for (const invoicesFilter of invoicesFilters) {
            if (invoicesFilter === InvoicesFilterType.OnlyAvailable) {
                buyerInvoiceStatuses.push(BuyerInvoiceStatus2.ELIGIBLE);
            }
            if (invoicesFilter === InvoicesFilterType.OnlyExpired) {
                buyerInvoiceStatuses.push(BuyerInvoiceStatus2.INELIGIBLE);
            }
            if (invoicesFilter === InvoicesFilterType.OnlyPaid) {
                buyerInvoiceStatuses.push(BuyerInvoiceStatus2.EARLY_PAID);
            }
            if (invoicesFilter === InvoicesFilterType.OnlyPayLater) {
                buyerInvoiceStatuses.push(BuyerInvoiceStatus2.PAY_LATER);
            }
            if (invoicesFilter === InvoicesFilterType.OnlyPending) {
                buyerInvoiceStatuses.push(BuyerInvoiceStatus2.PENDING);
            }
            if (invoicesFilter === InvoicesFilterType.OnlyClosed) {
                buyerInvoiceStatuses.push(
                    BuyerInvoiceStatus2.CLOSED,
                    BuyerInvoiceStatus2.EARLY_PAID_CLOSED
                );
                if (this._featureFlagService.isOnSync(FeatureFlags.PayLater)) {
                    buyerInvoiceStatuses.push(BuyerInvoiceStatus2.PAY_LATER_CLOSED);
                }
            }
            if (invoicesFilter === InvoicesFilterType.OnlyRevoked) {
                buyerInvoiceStatuses.push(BuyerInvoiceStatus2.REVOKED);
            }
        }
        return buyerInvoiceStatuses;
    }

    public isInvoiceClickable(row: InvoiceSummary): boolean {
        return (
            (row.isGroup &&
                !row.refGroupedInvoiceKey &&
                this._featureFlagService.isOnSync(FeatureFlags.PendingCreditMemosDetails)) ||
            this._featureFlagService.isOnSync(FeatureFlags.BuyerAccountInvoiceDetails)
        );
    }

    public async setFilterQueryParams(): Promise<void> {
        let tab = this._activatedRoute.snapshot.queryParamMap.get('tab');
        const queryParams = {
            ...TableHelper.getQueryParamsByListSource(this.listInvoicesDataSource),
            [FastTrackRouteQueryParams.InvoiceTypeFilter]:
                this.invoicesFiltersFormGroup.controls.invoicesFilter.value || undefined,
            [FastTrackRouteQueryParams.SearchText]:
                this.invoicesFiltersFormGroup.controls.invoiceNumber.value || undefined,
            [FastTrackRouteQueryParams.SupplierKeysFilter]:
                this.invoicesFiltersFormGroup.controls.sellerKeys.value || undefined,
            [FastTrackRouteQueryParams.BuyerKeysFilter]:
                this.invoicesFiltersFormGroup.controls.buyerKeys.value || undefined,
            tab
        };
        await this._router.navigate([], {
            relativeTo: this._activatedRoute,
            queryParams: queryParams,
            replaceUrl: true
        });
    }

    private _openInvoiceDetail(invoice: InvoiceSummary): void {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises

        const isGroup = invoice.isGroup && !invoice.refGroupedInvoiceKey;
        const groupedInvoiceDetailRoute = this.isFinancingAccountPortalUser
            ? SUPPLY_CHAIN_DIRECT_ACCOUNT_PARENT_GROUPED_INVOICE_DETAIL
            : SUPPLY_CHAIN_DIRECT_BUYER_PARENT_GROUPED_INVOICE_DETAIL;

        const groupedInvoiceType =
            invoice.finalApprovedAmount.cents < 0 ? 'creditMemo' : 'approval';

        this._router.navigateByUrl(
            this._router.createUrlTree(
                getDynamicRouterLink(
                    getRouterLinkById(
                        atlas,
                        isGroup ? groupedInvoiceDetailRoute : BUYER_ACCOUNT_INVOICE_DETAIL,
                        {
                            noStandaloneLeadingSlash: true
                        }
                    ),
                    { invoiceKey: invoice.refGroupedInvoiceKey || invoice.refInvoiceKey }
                ),
                {
                    queryParams: {
                        [FastTrackRouteQueryParams.PreviousRouteId]:
                            SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES,
                        [FastTrackRouteQueryParams.GroupedInvoiceType]: groupedInvoiceType
                    }
                }
            )
        );
    }

    public goSupplierDetails(invoice: InvoiceSummary) {
        this._router.navigateByUrl(
            this._router.createUrlTree(
                getDynamicRouterLink(
                    getRouterLinkById(
                        atlas,
                        this.isFinancingAccountPortalUser
                            ? ACCOUNT_SUPPLIER_DETAILS
                            : BUYER_SUPPLIER_DETAILS,
                        {
                            noStandaloneLeadingSlash: true
                        }
                    ),
                    { sellerKey: invoice.sellerKey || invoice.sellerName }
                ),
                {
                    queryParams: {
                        [FastTrackRouteQueryParams.PreviousRouteId]:
                            SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES
                    }
                }
            )
        );
    }

    private checkOpenAndRevokedCreditMemos(invoices: InvoiceSummary2[]) {
        this.onlyOpenAndRevokedCreditMemos = invoices.every(
            (invoice) =>
                invoice.invoiceNumber.includes('Open Credit Memos') ||
                invoice.invoiceNumber.includes('Revoked Credit Memos')
        );
    }
}
